import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	FinancialInstitutionChargeTypesList,
	getFilteredLenderFormsByLender,
} from '@core/util/lenderForms';
import { PDFForm, PDFForms, PDFFormTypes } from '@models/matter-form-data';
import {
	ChargeTypes,
	DocumentTypes,
	MatterMortgage,
	MatterMortgageLenderForm,
} from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { MatterMortgageLenderFormsRepoService } from '@repos/matter-mortgage-lender-forms/matter-mortgage-lender-forms.service';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { ICON } from '@core/util/icon';

export interface AddLenderFormModalData {
	ordinalNumber: string;
	mortgageId: string;
}

@UntilDestroy()
@Component({
	selector: 'app-add-lender-form-modal',
	templateUrl: 'add-lender-form-modal.component.html',
	styleUrls: ['./add-lender-form-modal.component.scss'],
	standalone: false,
})
export class AddLenderFormModalComponent implements OnInit {
	public _selectedFinancialInstitution;
	public selectedFinancialInstitutionName;

	public selectedChargeTypeName;
	public selectedDocumentTypeName;

	public chargeTypesList: RadioGroupItem<ChargeTypes>[] = [];
	public formsList: PDFForm[];
	public documentTypesList: RadioGroupItem<DocumentTypes>[] = [];

	public financialInstitutionsList = FinancialInstitutionChargeTypesList;

	public financialInstitutions = this.financialInstitutionsList.map((inst) => ({
		name: inst.institutionName,
		id: inst.institutionId,
	}));

	public mortgageId: string;
	public ordinalNumber: string;
	@Select(MatterMortgagesState.getMortgage) mortgageFilter$: Observable<
		(filter: string) => MatterMortgage
	>;

	public matterMortgage: MatterMortgage;
	public _matterMortgageId: string;

	public mortgage: MatterMortgage;
	public ICON = ICON;

	private lenderForms: MatterMortgageLenderForm[] = [];

	constructor(
		private matterMortgageLenderFormsRepoService: MatterMortgageLenderFormsRepoService,
		public dialogRef: MatDialogRef<AddLenderFormModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: AddLenderFormModalData,
	) {}

	ngOnInit(): void {
		this.mortgageId = this.data.mortgageId;
		this.ordinalNumber = this.data?.ordinalNumber;

		this.getMatterMortgage$(this.mortgageId)
			.pipe(untilDestroyed(this))
			.subscribe((matterMortgage) => {
				this.mortgage = matterMortgage;
				this.selectedChargeTypeName = this.mortgage?.forms?.chargeType;
				this.selectedDocumentTypeName = this.mortgage?.forms?.documentType;
				this._selectedFinancialInstitution =
					this.mortgage?.forms?.financialInstitutionId;
				this.selectedFinancialInstitutionName =
					this.financialInstitutionsList.find(
						(inst) =>
							inst.institutionId ===
							this.mortgage?.forms?.financialInstitutionId,
					)?.institutionName;

				this.getFinancialInstitutionFormList(
					this.getPDFFormType(
						this.selectedFinancialInstitution,
						this.selectedChargeTypeName,
						this.selectedDocumentTypeName,
					),
				);
			});

		this.matterMortgageLenderFormsRepoService
			.All({ mortgageId: this.mortgageId })
			.pipe(untilDestroyed(this))
			.subscribe((lenderForms) => {
				this.lenderForms = getFilteredLenderFormsByLender(
					this.mortgage,
					lenderForms as MatterMortgageLenderForm[],
				);
			});
	}

	getMatterMortgage$ = (id: string): Observable<MatterMortgage> =>
		this.mortgageFilter$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(id)),
		);

	getFinancialInstitutionFormList(PDFFormType: PDFFormTypes) {
		const result = Object.keys(PDFForms)
			.filter((key) => PDFForms[key].type === PDFFormType)
			.map((key) => PDFForms[key]);
		this.formsList = result;
	}

	toggleMortgageForms(formId: string) {
		const formIsSelected = this.isFormSelected(formId);
		if (formIsSelected) {
			this.unselectMortgageForms(formId);
		} else {
			this.selectMortgageForms(formId);
		}
	}

	selectMortgageForms(formId: string) {
		const lenderForm = this.lenderForms?.find(
			(lf) => lf.documentName === formId,
		);

		if (lenderForm) {
			void this.matterMortgageLenderFormsRepoService.Update(
				lenderForm.id,
				{ enabled: true },
				{ mortgageId: this.mortgageId },
			);
		} else {
			const mortgageForms: MatterMortgageLenderForm = {
				chargeType: this.selectedChargeTypeName,
				financialInstitutionId: this.selectedFinancialInstitution,
				documentName: formId,
				enabled: true,
			};
			void this.matterMortgageLenderFormsRepoService.Create(mortgageForms, {
				mortgageId: this.mortgageId,
			});
		}
	}

	unselectMortgageForms(formId: string) {
		const lenderForm = this.lenderForms?.find(
			(form) => form.documentName === formId,
		);
		void this.matterMortgageLenderFormsRepoService.Update(
			lenderForm.id,
			{ enabled: false },
			{ mortgageId: this.mortgageId },
		);
	}

	isFormSelected(formId) {
		const form = this.lenderForms?.find((lf) => lf.documentName === formId);

		if (form) {
			return true;
		}
	}

	getPDFFormType(financialInstitution, chargeType, documentType) {
		return PDFFormTypes[
			`${financialInstitution}${documentType ? `_${documentType}` : ''}${chargeType ? `_${chargeType}` : ''}`
		];
	}

	get selectedFinancialInstitution() {
		return this._selectedFinancialInstitution;
	}
}
